import { type CreateSupportTicket, PriorityEnum, useApiSupportV1TicketCreate, useTradeIDQueryParams } from 'api';
import { type FieldValues, Form, SelectField, type SubmitHandler, TextField, useForm } from 'form';
import { Alert, Box, Button, Grid } from 'ui';

import { getTeamRequestCategories } from '~/support/components/SupportIssueForm/getTeamRequestCategories';
import { priorityOptions, teamOptions } from '~/support/components/SupportIssueForm/options';
import { Success } from '~/support/components/SupportIssueForm/Success';

const defaultValues = {
  priority: PriorityEnum.Medium,
};
const useCreateSupportTicket = () => {
  const { mutateAsync, ...rest } = useApiSupportV1TicketCreate();
  const { companyId, dealId, inspectionId, loadName, purchaseOrderSlug, salesOrderSlug, shipmentName } =
    useTradeIDQueryParams();

  const createSupportTicket = async (values: FieldValues) => {
    await mutateAsync({
      data: {
        company_id: companyId,
        deal_id: dealId,
        description: values.description,
        inspection_id: inspectionId,
        load_name: loadName,
        priority: values.priority,
        purchase_order_slug: purchaseOrderSlug,
        request_category: values.request_category,
        sales_order_slug: salesOrderSlug,
        shipment_name: shipmentName,
        team: values.team,
      },
    });
  };

  return {
    createSupportTicket,
    ...rest,
  };
};

export const SupportIssueForm = () => {
  const { createSupportTicket, data: ticket, isPending, error } = useCreateSupportTicket();

  const form = useForm<CreateSupportTicket>({
    defaultValues,
  });

  const {
    formState: { isDirty },
    reset,
    watch,
  } = form;

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    await createSupportTicket(data);
    reset(data, { keepDefaultValues: false, keepValues: true });
  };
  const selectedTeam = watch('team');
  const requestCategoryOptions = getTeamRequestCategories(selectedTeam);

  if (ticket) {
    return <Success ticket={ticket} />;
  }

  return (
    <Form {...form} onSubmit={onSubmit}>
      {error ? (
        <Box paddingY={2}>
          <Alert severity={'error'}>{error.message}</Alert>
        </Box>
      ) : null}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <SelectField label="Team" name="team" options={teamOptions} required />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectField
            label="Request Category"
            name="request_category"
            options={requestCategoryOptions}
            required
            disabled={!selectedTeam}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectField label="Priority" name="priority" options={priorityOptions} required />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Description" name="description" multiline rows={10} required />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" paddingBlockEnd={3}>
            <Button type="submit" size="large" loading={isPending} disabled={!isDirty}>
              Create Support Issue
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Form>
  );
};
